import * as d3 from 'd3';
import { Gift, Loader2, Lock, Sparkles, Star, TrendingUp, Zap } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface SpinOption {
  id: number;
  label: string;
  prize_type: string;
  prize_value: number;
  color: string;
}

interface SpinStatus {
  spinCredit: number;
  spinsAvailable: number;
  progressToNextSpin: number;
  threshold: number;
  amountNeeded: number;
  canSpin: boolean;
}

const SpinWheel: React.FC = () => {
  const { user, setShowAuthModal } = useAuth();
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<{ label: string; type: string; value: number } | null>(null);
  const [options, setOptions] = useState<SpinOption[]>([]);
  const [spinStatus, setSpinStatus] = useState<SpinStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [rotation, setRotation] = useState(0);

  const width = 380;
  const height = 380;
  const radius = Math.min(width, height) / 2;

  // Fetch spin options
  useEffect(() => {
    fetchOptions();
  }, []);

  // Fetch spin status when user changes
  useEffect(() => {
    if (user) {
      fetchSpinStatus();
    } else {
      setSpinStatus(null);
    }
  }, [user]);

  // Draw wheel when options change
  useEffect(() => {
    if (!svgRef.current || options.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const g = svg.append('g').attr('transform', `translate(${width / 2}, ${height / 2})`);

    const pie = d3
      .pie<SpinOption>()
      .value(() => 1)
      .sort(null);

    const arc = d3
      .arc<d3.PieArcDatum<SpinOption>>()
      .innerRadius(30)
      .outerRadius(radius - 15);

    const arcs = g.selectAll('arc').data(pie(options)).enter().append('g').attr('class', 'arc');

    arcs
      .append('path')
      .attr('d', arc)
      .attr('fill', (d) => d.data.color)
      .attr(
        'stroke',
        getComputedStyle(document.documentElement).getPropertyValue('--wheel-stroke').trim()
      )
      .attr('stroke-width', '3px')
      .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))');

    arcs
      .append('text')
      .attr('transform', (d) => {
        const pos = arc.centroid(d);
        const x = pos[0] * 1.3;
        const y = pos[1] * 1.3;
        const angle = ((d.startAngle + d.endAngle) / 2) * (180 / Math.PI) + 90;
        return `translate(${x},${y}) rotate(${angle})`;
      })
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', 'white')
      .attr('font-weight', 'bold')
      .attr('font-size', '11px')
      .style('text-shadow', '0 1px 2px rgba(0,0,0,0.5)')
      .text((d) => d.data.label);
  }, [options]);

  const fetchOptions = async () => {
    try {
      const response = await fetch('/api/spin/options');
      const data = await response.json();
      setOptions(data.options || []);
    } catch {
      // Use fallback options
      setOptions([
        {
          id: 1,
          label: '10% Off',
          prize_type: 'discount_percent',
          prize_value: 10,
          color: '#22c55e',
        },
        { id: 2, label: '$2 Off', prize_type: 'discount_fixed', prize_value: 2, color: '#3b82f6' },
        { id: 3, label: 'Free Item', prize_type: 'free_item', prize_value: 0, color: '#a855f7' },
        {
          id: 4,
          label: '25% Off',
          prize_type: 'discount_percent',
          prize_value: 25,
          color: '#f59e0b',
        },
        { id: 5, label: 'Try Again', prize_type: 'nothing', prize_value: 0, color: '#64748b' },
        { id: 6, label: '$5 Off', prize_type: 'discount_fixed', prize_value: 5, color: '#ec4899' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSpinStatus = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) return;

    try {
      const response = await fetch('/api/spin/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSpinStatus(data);
      }
    } catch {
      console.error('Failed to fetch spin status');
    }
  };

  const spin = async () => {
    if (spinning) return;

    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!spinStatus?.canSpin) {
      setError(
        `Complete $${spinStatus?.amountNeeded?.toFixed(2) || '20.00'} more in orders to earn a spin!`
      );
      return;
    }

    setSpinning(true);
    setResult(null);
    setError(null);

    const token = localStorage.getItem('sessionToken');

    try {
      const response = await fetch('/api/spin/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Spin failed');
      }

      // Calculate rotation to land on prize
      const sliceAngle = 360 / options.length;
      const prizeAngle = sliceAngle * data.prizeIndex + sliceAngle / 2;
      const extraSpins = 360 * 6;
      const newRotation = rotation + extraSpins + (360 - prizeAngle) + Math.random() * 20;

      setRotation(newRotation);

      setTimeout(() => {
        setSpinning(false);
        setResult(data.prize);
        fetchSpinStatus(); // Refresh status
      }, 4500);
    } catch (err) {
      setSpinning(false);
      setError(err instanceof Error ? err.message : 'Spin failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[85vh] flex flex-col items-center justify-center py-12 px-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="bg-orb w-96 h-96 bg-yellow-500 -top-32 -left-32" />
      <div
        className="bg-orb w-80 h-80 bg-purple-500 top-1/3 -right-40"
        style={{ animationDelay: '2s' }}
      />
      <div
        className="bg-orb w-64 h-64 bg-pink-500 bottom-20 left-20"
        style={{ animationDelay: '4s' }}
      />

      {/* Header */}
      <div className="mb-6 text-center relative z-10">
        <h1 className="text-4xl md:text-5xl font-black mb-4 flex items-center justify-center gap-3">
          <Sparkles className="w-10 h-10 text-accent-yellow animate-pulse" />
          <span className="text-gradient-animated">Prize Wheel</span>
          <Sparkles className="w-10 h-10 text-accent-yellow animate-pulse" />
        </h1>
        <p className="text-lg" style={{ color: 'var(--text-muted)' }}>
          Earn spins by completing <span className="text-success font-bold">$20</span> in orders!
        </p>
      </div>

      {/* Spin Status Card */}
      {user && spinStatus && (
        <div
          className="mb-6 glass rounded-2xl p-4 relative z-10 w-full max-w-sm"
          style={{ borderColor: 'var(--border-color)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Spins Available
            </span>
            <span className="text-2xl font-black text-accent-yellow">
              {spinStatus.spinsAvailable}
            </span>
          </div>
          <div className="mb-2">
            <div
              className="flex justify-between text-xs mb-1"
              style={{ color: 'var(--text-muted)' }}
            >
              <span>Progress to next spin</span>
              <span>
                ${spinStatus.progressToNextSpin.toFixed(2)} / ${spinStatus.threshold}
              </span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ backgroundColor: 'var(--surface-inactive)' }}
            >
              <div
                className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-500"
                style={{
                  width: `${(spinStatus.progressToNextSpin / spinStatus.threshold) * 100}%`,
                }}
              />
            </div>
          </div>
          {spinStatus.amountNeeded > 0 && spinStatus.spinsAvailable === 0 && (
            <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
              <TrendingUp className="w-3 h-3" />${spinStatus.amountNeeded.toFixed(2)} more to earn a
              spin
            </p>
          )}
        </div>
      )}

      {/* Not logged in message */}
      {!user && (
        <div className="mb-6 glass rounded-2xl p-4 border border-yellow-500/30 relative z-10 w-full max-w-sm text-center">
          <Lock className="w-6 h-6 text-accent-yellow mx-auto mb-2" />
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Sign in to track your spins and win prizes!
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="mt-3 text-accent-yellow font-bold text-sm hover:opacity-80"
          >
            Sign In / Register
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-error rounded-xl text-sm relative z-10 max-w-sm text-center">
          {error}
        </div>
      )}

      {/* Wheel Container */}
      <div className="relative mb-8 z-10">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full blur-3xl opacity-30 scale-110" />

        {/* Pointer */}
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-20">
          <div
            className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[40px] border-t-yellow-400"
            style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))' }}
          />
        </div>

        {/* Wheel SVG */}
        <div
          className="relative wheel-glow"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 4.5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
          }}
        >
          <svg ref={svgRef} width={width} height={height} />
        </div>

        {/* Center Knob */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-br from-slate-700 to-slate-900 rounded-full shadow-2xl flex items-center justify-center border-4 border-slate-600 z-10">
          <Gift className="text-yellow-400 w-8 h-8" />
        </div>
      </div>

      {/* Result / Spin Button */}
      <div className="h-32 flex items-center justify-center relative z-10">
        {result ? (
          <div className="text-center animate-bounce">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Star className="w-8 h-8 text-accent-yellow animate-spin-slow" />
              <h3 className="text-3xl font-black text-gradient">You Won!</h3>
              <Star className="w-8 h-8 text-accent-yellow animate-spin-slow" />
            </div>
            <div className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {result.label}
            </div>
            {result.type !== 'nothing' && (
              <p className="text-success text-sm">Prize applied to your account!</p>
            )}
          </div>
        ) : (
          <button
            onClick={spin}
            disabled={spinning || (user && spinStatus && !spinStatus.canSpin)}
            className="group relative px-12 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-full blur-lg opacity-75 group-hover:opacity-100 group-disabled:opacity-30 transition-opacity" />
            <div
              className="relative btn-neon bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 font-black text-xl rounded-full"
              style={{ color: '#0f172a' }}
            >
              {!user ? (
                <>
                  <Lock className="w-6 h-6 inline mr-2" />
                  Sign In to Spin
                </>
              ) : spinning ? (
                'Good Luck!'
              ) : spinStatus?.canSpin ? (
                <>
                  <Zap className="w-6 h-6 inline mr-2" />
                  SPIN NOW!
                </>
              ) : (
                <>
                  <Lock className="w-6 h-6 inline mr-2" />
                  Earn a Spin
                </>
              )}
            </div>
          </button>
        )}
      </div>

      {/* Decorative stars */}
      {[...Array(8)].map((_, i) => (
        <Star
          key={i}
          className="absolute text-yellow-400/30 animate-pulse"
          style={{
            width: 16 + Math.random() * 16,
            height: 16 + Math.random() * 16,
            left: `${10 + Math.random() * 80}%`,
            top: `${10 + Math.random() * 80}%`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        />
      ))}
    </div>
  );
};

export default SpinWheel;
